/**
 * Bulk Upload Configurations
 *
 * Configuration for different bulk upload operations:
 * - Bulk Data Fetch
 * - Bulk Revoke Consent
 * - Bulk Create Consent
 */

import type { BulkUploadConfig, CustomValidationRule } from '../types/bulk-upload';
import { isValidDate, validateDateRange } from '../utils/bulk-upload-validators';

/**
 * Date range validation rule (both dates must be present or both empty, from <= to)
 */
const dateRangeValidation: CustomValidationRule = {
  name: 'dateRangeValidation',
  validate: (rowData) => {
    const fromDate = rowData.fiDataRangeFrom || '';
    const toDate = rowData.fiDataRangeTo || '';

    const hasFromDate = fromDate && fromDate !== '';
    const hasToDate = toDate && toDate !== '';

    // Both or none
    if (hasFromDate !== hasToDate) {
      return 'Both FI Data Range From and FI Data Range To must be provided together, or both left empty';
    }

    // Range validation (from <= to)
    if (hasFromDate && hasToDate && isValidDate(fromDate) && isValidDate(toDate)) {
      if (!validateDateRange(fromDate, toDate)) {
        return 'FI Data Range From must be less than or equal to FI Data Range To';
      }
    }

    return null;
  },
};

/**
 * Bulk Data Fetch Configuration
 */
export const BULK_DATA_FETCH_CONFIG: BulkUploadConfig = {
  operationType: 'data_fetch',
  operationDisplayName: 'Data Fetch',
  description: 'Upload CSV or JSON file with consent IDs and fetch parameters',
  sampleTemplateUrl: '/assets/data-fetch-sample.xlsx',
  sampleTemplateFilename: 'data-fetch-sample.xlsx',
  screenName: 'data_fetch',
  uploadEndpoint: 'bulk/fileUpload/dataFetch',

  columns: [
    {
      name: 'consentId',
      required: true,
      type: 'uuid',
      displayName: 'Consent ID',
    },
    {
      name: 'fiDataRangeFrom',
      required: false,
      type: 'date',
      displayName: 'FI Data Range From',
    },
    {
      name: 'fiDataRangeTo',
      required: false,
      type: 'date',
      displayName: 'FI Data Range To',
    },
  ],

  checkDuplicatesInColumn: 'consentId',

  customValidations: [dateRangeValidation],

  additionalFields: {
    referenceId: {
      required: true,
      label: 'Reference ID',
      placeholder: 'Enter reference ID (alphanumeric, _, -, spaces, parens)',
      minLength: 5,
      maxLength: 70,
      pattern: /^[a-zA-Z0-9_-][a-zA-Z0-9_ ()-]*$/,
      errorMessage: 'Must start with alphanumeric/underscore/hyphen. Spaces and parentheses allowed.',
    },
    configId: {
      required: false,
      label: 'Config ID',
      placeholder: 'Enter config ID',
    },
    dateRange: {
      required: false,
      fromLabel: 'FI-Start-Date',
      toLabel: 'FI-End-Date',
      validateRange: true,
    },
    // Phase 1: Output options for analytics/EWS
    outputOptions: {
      enabled: true,
      label: 'Output Options',
      helpText: 'Select what outputs you want to generate',
      analyticsLabel: 'Enable Analytics',
      ewsLabel: 'Enable EWS (Early Warning System)',
      formatLabel: 'Output Format',
    },
  },
};

/**
 * Bulk Revoke Consent Configuration
 */
export const BULK_REVOKE_CONSENT_CONFIG: BulkUploadConfig = {
  operationType: 'revoke_consent',
  operationDisplayName: 'Revoke Consent',
  description: 'Upload CSV or JSON file with consent IDs to revoke',
  sampleTemplateUrl: '/assets/consent-revoke-sample.xlsx',
  sampleTemplateFilename: 'consent-revoke-sample.xlsx',
  screenName: 'revoke_consent',
  uploadEndpoint: 'bulk/fileUpload/revokeConsent',

  columns: [
    {
      name: 'consentId',
      required: true,
      type: 'uuid',
      displayName: 'Consent ID',
    },
  ],

  checkDuplicatesInColumn: 'consentId',

  additionalFields: {
    referenceId: {
      required: true,
      label: 'Reference ID',
      placeholder: 'Enter reference ID (alphanumeric, _, -, spaces, parens)',
      minLength: 5,
      maxLength: 70,
      pattern: /^[a-zA-Z0-9_-][a-zA-Z0-9_ ()-]*$/,
      errorMessage: 'Must start with alphanumeric/underscore/hyphen. Spaces and parentheses allowed.',
    },
  },
};

/**
 * Bulk Create Consent Configuration
 *
 * Columns based on Angular sample file (consent-request-sample.csv):
 * - vua: Customer Virtual User Address (e.g., 1234567890@aa)
 * - accountId: View Account ID
 * - consentTemplateList: Consent Template(s), can have multiple separated by |
 * - rdURL: Redirection URL
 */
export const BULK_CREATE_CONSENT_CONFIG: BulkUploadConfig = {
  operationType: 'create_consent',
  operationDisplayName: 'Consent Request',
  description: 'Please upload the document from which consents will be requested',
  sampleTemplateUrl: '/assets/consent-request-sample.csv',
  sampleTemplateFilename: 'consent-request-sample.csv',
  screenName: 'consent_request',
  uploadEndpoint: 'bulk/fileUpload/requestConsent',

  columns: [
    {
      name: 'vua',
      required: true,
      type: 'string',
      displayName: 'VUA (Virtual User Address)',
      validator: (value) => {
        // VUA format validation - should contain @ for AA identifier
        if (!value || value.length < 3) {
          return 'VUA must be at least 3 characters';
        }
        if (!value.includes('@')) {
          return 'VUA should be in format: identifier@aa (e.g., 1234567890@aa)';
        }
        return null;
      },
    },
    {
      name: 'accountId',
      required: true,
      type: 'string',
      displayName: 'Account ID',
      validator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Account ID is required';
        }
        return null;
      },
    },
    {
      name: 'consentTemplateList',
      required: true,
      type: 'string',
      displayName: 'Consent Template List',
      validator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Consent Template List is required';
        }
        // Can have multiple templates separated by |
        return null;
      },
    },
    {
      name: 'rdURL',
      required: false,
      type: 'string',
      displayName: 'Redirection URL',
    },
  ],

  // Duplicate check disabled for bulk consent - VUA + accountId combinations may legitimately repeat
  checkDuplicatesInColumn: undefined,

  customValidations: [],

  additionalFields: {
    referenceId: {
      required: true,
      label: 'Batch Reference ID',
      placeholder: 'Enter batch reference ID',
      minLength: 5,
      maxLength: 70,
      pattern: /^[a-zA-Z0-9_-][a-zA-Z0-9_ ()-]*$/,
      errorMessage: 'Must start with alphanumeric/underscore/hyphen. Spaces and parentheses allowed.',
    },
  },
};

/**
 * Get configuration by operation type
 */
export const getBulkUploadConfig = (
  operationType: 'data_fetch' | 'revoke_consent' | 'create_consent'
): BulkUploadConfig => {
  switch (operationType) {
    case 'data_fetch':
      return BULK_DATA_FETCH_CONFIG;
    case 'revoke_consent':
      return BULK_REVOKE_CONSENT_CONFIG;
    case 'create_consent':
      return BULK_CREATE_CONSENT_CONFIG;
    default:
      throw new Error(`Unknown operation type: ${operationType}`);
  }
};
